import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Plus, Package, Layers } from 'lucide-react';
import { toast } from 'sonner';

interface Module {
  id: string;
  name: string;
  description: string;
}

interface SubModule {
  id: string;
  name: string;
  module_id: string;
  description: string;
}

interface CustomPlanBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modules: Module[];
  subModules: SubModule[];
  onCreatePlan: (planData: {
    name: string;
    code: string;
    modules: string[];
    subModules: string[];
  }) => void;
  isCreating?: boolean;
}

export function CustomPlanBuilder({
  open,
  onOpenChange,
  modules,
  subModules,
  onCreatePlan,
  isCreating = false
}: CustomPlanBuilderProps) {
  const [planName, setPlanName] = useState('');
  const [planCode, setPlanCode] = useState('');
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  const [selectedSubModules, setSelectedSubModules] = useState<Set<string>>(new Set());

  const handleModuleToggle = (moduleId: string, checked: boolean) => {
    const newSelectedModules = new Set(selectedModules);
    const moduleSubModules = subModules.filter(sm => sm.module_id === moduleId);
    
    if (checked) {
      newSelectedModules.add(moduleId);
    } else {
      newSelectedModules.delete(moduleId);
      // Remove all sub-modules for this module
      moduleSubModules.forEach(sm => {
        setSelectedSubModules(prev => {
          const newSet = new Set(prev);
          newSet.delete(sm.id);
          return newSet;
        });
      });
    }
    
    setSelectedModules(newSelectedModules);
  };

  const handleSubModuleToggle = (subModuleId: string, checked: boolean) => {
    const subModule = subModules.find(sm => sm.id === subModuleId);
    if (!subModule) return;

    const newSelectedSubModules = new Set(selectedSubModules);
    
    if (checked) {
      // Ensure parent module is selected
      if (!selectedModules.has(subModule.module_id)) {
        setSelectedModules(prev => new Set(prev.add(subModule.module_id)));
      }
      newSelectedSubModules.add(subModuleId);
    } else {
      newSelectedSubModules.delete(subModuleId);
    }
    
    setSelectedSubModules(newSelectedSubModules);
  };

  const handleCreate = () => {
    if (!planName.trim() || !planCode.trim()) {
      toast.error('Plan name and code are required');
      return;
    }

    if (selectedModules.size === 0) {
      toast.error('At least one module must be selected');
      return;
    }

    onCreatePlan({
      name: planName.trim(),
      code: planCode.trim().toLowerCase().replace(/\s+/g, '_'),
      modules: Array.from(selectedModules),
      subModules: Array.from(selectedSubModules)
    });
  };

  const handleReset = () => {
    setPlanName('');
    setPlanCode('');
    setSelectedModules(new Set());
    setSelectedSubModules(new Set());
  };

  const groupedSubModules = modules.reduce((acc, module) => {
    acc[module.id] = subModules.filter(sm => sm.module_id === module.id);
    return acc;
  }, {} as Record<string, SubModule[]>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Create Custom Plan
          </DialogTitle>
          <DialogDescription>
            Build a custom plan by selecting specific modules and sub-modules for your organization's needs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Plan Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plan-name">Plan Name</Label>
              <Input
                id="plan-name"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="e.g., Custom Enterprise Plan"
              />
            </div>
            <div>
              <Label htmlFor="plan-code">Plan Code</Label>
              <Input
                id="plan-code"
                value={planCode}
                onChange={(e) => setPlanCode(e.target.value)}
                placeholder="e.g., custom_enterprise"
              />
            </div>
          </div>

          <Separator />

          {/* Module Selection */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Layers className="h-4 w-4" />
              <h3 className="text-lg font-medium">Module Selection</h3>
              <Badge variant="outline">
                {selectedModules.size} of {modules.length} selected
              </Badge>
            </div>

            <div className="grid gap-4">
              {modules.map((module) => {
                const isModuleSelected = selectedModules.has(module.id);
                const moduleSubModules = groupedSubModules[module.id] || [];
                const selectedSubModulesCount = moduleSubModules.filter(sm => 
                  selectedSubModules.has(sm.id)
                ).length;

                return (
                  <Card key={module.id} className={isModuleSelected ? 'ring-2 ring-primary/20' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id={`module-${module.id}`}
                          checked={isModuleSelected}
                          onCheckedChange={(checked) => 
                            handleModuleToggle(module.id, checked as boolean)
                          }
                        />
                        <div className="flex-1">
                          <CardTitle className="text-base">{module.name}</CardTitle>
                          <CardDescription>{module.description}</CardDescription>
                        </div>
                        {moduleSubModules.length > 0 && (
                          <Badge variant="secondary">
                            {selectedSubModulesCount}/{moduleSubModules.length} sub-modules
                          </Badge>
                        )}
                      </div>
                    </CardHeader>

                    {/* Sub-modules */}
                    {isModuleSelected && moduleSubModules.length > 0 && (
                      <CardContent>
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-muted-foreground">Sub-modules</h4>
                          <div className="grid gap-2">
                            {moduleSubModules.map((subModule) => (
                              <div key={subModule.id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50">
                                <Checkbox
                                  id={`submodule-${subModule.id}`}
                                  checked={selectedSubModules.has(subModule.id)}
                                  onCheckedChange={(checked) => 
                                    handleSubModuleToggle(subModule.id, checked as boolean)
                                  }
                                />
                                <div className="flex-1">
                                  <Label
                                    htmlFor={`submodule-${subModule.id}`}
                                    className="text-sm font-medium cursor-pointer"
                                  >
                                    {subModule.name}
                                  </Label>
                                  <p className="text-xs text-muted-foreground">
                                    {subModule.description}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Plan Summary */}
          {selectedModules.size > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Plan Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Total Modules:</span>
                    <Badge>{selectedModules.size}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Total Sub-modules:</span>
                    <Badge>{selectedSubModules.size}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button 
            onClick={handleCreate}
            disabled={isCreating || !planName.trim() || selectedModules.size === 0}
          >
            {isCreating ? 'Creating...' : 'Create Plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}